import { getBaseUrlWithoutApi } from '../config/api';

/**
 * On an HTTPS page, browsers block passive content like <img src="http://..."> (mixed content).
 * Mobile apps are not subject to that rule — the same URL can work in-app but look blank on web.
 * Upgrade known media hosts to HTTPS; optional `pageIsHttps` is for unit tests.
 */
export function upgradeMediaUrlToHttpsIfNeeded(
  url: string,
  opts?: { pageIsHttps?: boolean }
): string {
  const u = (url || '').trim();
  if (!u) return u;
  const pageIsHttps =
    opts?.pageIsHttps ??
    (typeof window !== 'undefined' && window.location?.protocol === 'https:');
  if (!pageIsHttps || !/^http:\/\//i.test(u)) return u;

  let parsed: URL;
  try {
    parsed = new URL(u);
  } catch {
    return u;
  }

  const h = parsed.hostname.toLowerCase();
  const cloudinaryHost = h === 'res.cloudinary.com' || h.endsWith('.cloudinary.com');
  const extras = (process.env.REACT_APP_CLOUDINARY_MEDIA_HOSTS || '')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  const extraCname = extras.includes(h);

  let apiHostMatch = false;
  try {
    const apiOrigin = new URL(getBaseUrlWithoutApi());
    apiHostMatch = apiOrigin.hostname === h;
  } catch {
    /* ignore */
  }

  if (cloudinaryHost || extraCname || apiHostMatch) {
    parsed.protocol = 'https:';
    return parsed.href;
  }

  return u;
}

/**
 * Turn API-relative media paths into absolute URLs the browser can load
 * (e.g. `/uploads/...` must use the API host, not the SPA origin).
 */
export function resolvePublicMediaUrl(url: string): string {
  const u = (url || '').trim();
  if (!u) return u;
  let out: string;
  if (/^https?:\/\//i.test(u)) {
    out = u;
  } else if (u.startsWith('//')) {
    out = `https:${u}`;
  } else if (u.startsWith('/')) {
    const base = getBaseUrlWithoutApi().replace(/\/$/, '');
    out = `${base}${u}`;
  } else {
    return u;
  }
  return upgradeMediaUrlToHttpsIfNeeded(out);
}

/** Coerce API / Mongoose shapes to a single displayable URL string. */
export function normalizeMediaUrlString(raw: unknown): string {
  if (raw == null) return '';
  if (typeof raw === 'string') return raw.trim();
  if (typeof raw === 'object' && raw !== null && 'url' in (raw as Record<string, unknown>)) {
    const inner = (raw as { url?: unknown }).url;
    if (typeof inner === 'string') return inner.trim();
  }
  return '';
}

const IMAGE_UPLOAD_MARKER = '/image/upload/';

/** Cloudinary output-format token (after optional leading chain segment). */
const CLOUDINARY_OUTPUT_FORMAT_TOKEN =
  /(^|[/,])f_(png8|png24|jpeg|jpg|png|webp|avif|auto|gif|ico|svg|bmp|tiff|tif|pdf|heic|heif|jxr|wdp|jp2|hdp)(\/|,|\?|#|$)/i;

const LEADING_OUTPUT_FORMAT =
  /^f_(png8|png24|jpeg|jpg|png|webp|avif|auto|gif|ico|svg|bmp|tiff|tif|pdf|heic|heif|jxr|wdp|jp2|hdp)(\/|,|\?|#|$)/i;

function parseHttpUrl(url: string): URL | null {
  try {
    return new URL(url);
  } catch {
    return null;
  }
}

/**
 * Optional comma-separated hostnames (e.g. custom Cloudinary CNAME) via
 * REACT_APP_CLOUDINARY_MEDIA_HOSTS — must match URL hostname exactly.
 */
function isCloudinaryDeliveryHostname(hostname: string): boolean {
  const h = hostname.toLowerCase();
  if (h === 'res.cloudinary.com') return true;
  const extras = (process.env.REACT_APP_CLOUDINARY_MEDIA_HOSTS || '')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  return extras.includes(h);
}

/**
 * Do not rewrite URLs that are likely signed — changing the path or query can invalidate auth.
 * (Avoid broad `s=` heuristics: they can skip rewrites on benign long query params.)
 */
function isProbablySignedCloudinaryDeliveryUrl(url: string): boolean {
  if (/\/s--[^/]+--\//i.test(url)) return true;
  const parsed = parseHttpUrl(url);
  if (!parsed) return false;
  const keys = new Set(
    Array.from(parsed.searchParams.keys(), (k) => k.toLowerCase())
  );
  if (keys.has('signature')) return true;
  if (keys.has('token')) return true;
  if (keys.has('sign_url')) return true;
  if (keys.has('api_key') && keys.has('timestamp')) return true;
  return false;
}

/**
 * Part of the URL after `/image/upload/` that is only transformation segments,
 * ending before the first `v{digits}/` version folder.
 *
 * **Product assumption:** stored portfolio URLs use Cloudinary’s usual versioned form
 * (`…/image/upload/<optional transforms>/v1234567/...`). That lets us ignore commas in the
 * public_id when detecting mid-chain `f_webp` / `f_jpg` etc. If a URL has **no** `v{digits}/`
 * segment, we only treat an output format as “already set” when it appears at the **start**
 * of the chain (`LEADING_OUTPUT_FORMAT`); exotic unversioned paths are not fully analyzed.
 */
function cloudinaryTransformHead(rest: string): string {
  const m = /\bv\d+\//.exec(rest);
  if (!m || m.index === undefined || m.index <= 0) return '';
  return rest.slice(0, m.index);
}

function transformChainHasExplicitOutputFormat(rest: string): boolean {
  if (LEADING_OUTPUT_FORMAT.test(rest)) return true;
  const head = cloudinaryTransformHead(rest);
  return head.length > 0 && CLOUDINARY_OUTPUT_FORMAT_TOKEN.test(head);
}

/**
 * Full URL for <img>: resolve relative paths, then request browser-safe Cloudinary delivery.
 */
export function resolveWebImageSrc(raw: unknown): string {
  const s = normalizeMediaUrlString(raw);
  if (!s) return '';
  return upgradeMediaUrlToHttpsIfNeeded(
    ensureWebFriendlyImageUrl(resolvePublicMediaUrl(s))
  );
}

/**
 * Cloudinary portfolio URLs from iPhone uploads are often HEIC/HEIF. Mobile decoders
 * handle those; Chrome and most desktop browsers show a blank <img> unless we request
 * a delivered format (f_auto / f_jpg). Do not rewrite signed URLs (path or query).
 */
export function ensureWebFriendlyImageUrl(url: string): string {
  const u = (url || '').trim();
  if (!/^https?:\/\//i.test(u)) return u;

  const parsed = parseHttpUrl(u);
  if (!parsed || !isCloudinaryDeliveryHostname(parsed.hostname)) {
    return upgradeMediaUrlToHttpsIfNeeded(u);
  }

  const idx = u.toLowerCase().indexOf(IMAGE_UPLOAD_MARKER);
  if (idx === -1) return upgradeMediaUrlToHttpsIfNeeded(u);

  if (isProbablySignedCloudinaryDeliveryUrl(u)) return upgradeMediaUrlToHttpsIfNeeded(u);

  const rest = u.slice(idx + IMAGE_UPLOAD_MARKER.length);
  if (transformChainHasExplicitOutputFormat(rest)) return upgradeMediaUrlToHttpsIfNeeded(u);

  // Known HEIC/HEIF in path — force JPEG for maximum desktop compatibility.
  if (/\.(heic|heif)(\?|#|$|\/)/i.test(u)) {
    return upgradeMediaUrlToHttpsIfNeeded(
      `${u.slice(0, idx + IMAGE_UPLOAD_MARKER.length)}f_jpg,q_auto/${rest}`
    );
  }

  // Prefer auto format (webp/avif/jpeg) so <img> works even when the path has no extension.
  return upgradeMediaUrlToHttpsIfNeeded(
    `${u.slice(0, idx + IMAGE_UPLOAD_MARKER.length)}f_auto,q_auto/${rest}`
  );
}

export function isProbablyVideoUrl(url: string): boolean {
  return /\.(mp4|webm|mov|m4v|ogv)(\?|#|$)/i.test((url || '').trim());
}
